/**
 * Generate copy-paste install snippets for a widget instance, for all three
 * integration modes, plus a Content-Security-Policy block the customer can add.
 *
 * Pure functions (no DOM / React) so they're trivially unit-tested. The widget
 * is a single ESM bundle `hotline.js` loaded with `type="module"`; it detects its
 * mode at runtime from the `data-mode` attribute (the same `data-public-key` /
 * `data-mode` attributes the widget's own config discovery reads — see
 * apps/widget/src/config.ts). The difference between modes is purely how the
 * customer references it.
 *
 *  1. embed     — a module `<script>` tag with `data-public-key`; auto-mounts.
 *  2. floating  — the same tag with `data-mode="floating"` (floating launcher).
 *  3. bookmarklet — a `javascript:` URL that injects the module script.
 */

/** Inputs needed to render snippets. */
/**
 * `URL` is a global in every runtime this package targets, but the shared lib
 * config (`lib: ["ES2023"]`, `types: []`) deliberately declares neither DOM nor
 * Node. Declare exactly the surface used — erased at compile time, resolves to
 * the real global at runtime (the contracts/origin.ts precedent).
 */
interface ParsedUrl {
  origin: string;
  host: string;
}
declare const URL: new (input: string) => ParsedUrl;

export interface SnippetParams {
  /** The instance's public key (`pk_*`). */
  publicKey: string;
  /** Base URL the widget bundle is served from (no trailing slash needed). */
  cdnUrl: string;
  /** Backend API origin used for the CSP connect-src directive. */
  apiUrl: string;
  /** Instance label, so the AI prompt can name what it is wiring up. */
  instanceName?: string;
  /** Origins already on the allowlist — the prompt reminds about the missing ones. */
  allowedOrigins?: string[];
}

/** Strip a trailing slash for clean URL joins. */
function trimSlash(u: string): string {
  return u.replace(/\/+$/, '');
}

/** Absolute URL of the ESM widget bundle (loaded as `type="module"`). */
export function bundleUrl(cdnUrl: string): string {
  return `${trimSlash(cdnUrl)}/hotline.js`;
}

/** Mode 1 — module `<script>` embed with the public key + explicit mode. */
export function embedSnippet({ publicKey, cdnUrl }: SnippetParams): string {
  return [
    `<script`,
    `  type="module"`,
    `  src="${bundleUrl(cdnUrl)}"`,
    `  data-public-key="${publicKey}"`,
    `  data-mode="embed"`,
    `></script>`,
  ].join('\n');
}

/** Mode 2 — floating launcher: the same module script with `data-mode="floating"`. */
export function floatingSnippet({ publicKey, cdnUrl }: SnippetParams): string {
  return [
    `<script`,
    `  type="module"`,
    `  src="${bundleUrl(cdnUrl)}"`,
    `  data-public-key="${publicKey}"`,
    `  data-mode="floating"`,
    `></script>`,
  ].join('\n');
}

/**
 * Mode 3 — bookmarklet. A single-line `javascript:` URL that injects the module
 * bundle with the key + mode baked in. Suitable for "drag to bookmarks bar".
 */
export function bookmarkletSnippet({ publicKey, cdnUrl }: SnippetParams): string {
  const loader =
    `(function(){` +
    `var s=document.createElement('script');` +
    `s.type='module';` +
    `s.src='${bundleUrl(cdnUrl)}';` +
    `s.setAttribute('data-public-key','${publicKey}');` +
    `s.setAttribute('data-mode','bookmarklet');` +
    `document.body.appendChild(s);` +
    `})();`;
  return `javascript:${encodeURIComponent(loader)}`;
}

/**
 * A Content-Security-Policy block the customer can merge into their site CSP so
 * the widget can load its module bundle, talk to the API (REST + WS), capture a
 * screenshot, and run its audio worklet for voice.
 */
export function cspBlock({ cdnUrl, apiUrl }: SnippetParams): string {
  const cdn = trimSlash(cdnUrl);
  const api = trimSlash(apiUrl);
  // Derive ws(s):// origin for the live/realtime sockets.
  const wsApi = api.replace(/^http/, 'ws');
  return [
    `Content-Security-Policy:`,
    `  script-src 'self' ${cdn};`,
    `  connect-src 'self' ${api} ${wsApi};`,
    `  img-src 'self' ${api} data: blob:;`,
    `  style-src 'self' 'unsafe-inline';`,
    `  worker-src 'self' blob:;`,
  ].join('\n');
}

/** A labeled snippet (mode + language hint + body) for rendering. */
/**
 * A one-line self-test the customer can paste into a terminal.
 *
 * `/v1/diagnose` is deliberately not origin-gated, so unlike every other endpoint
 * it answers even when the allowlist is the thing that is wrong — which is exactly
 * when someone needs it.
 */
export function diagnoseSnippet({ publicKey, apiUrl, allowedOrigins }: SnippetParams): string {
  const origin = allowedOrigins?.[0] ?? 'https://your.site';
  return `curl -s "${trimSlash(apiUrl)}/v1/diagnose?publicKey=${publicKey}" \\\n  -H "Origin: ${origin}"`;
}


/** Absolute URL of the classic tag-manager loader. */
export function loaderUrl(cdnUrl: string): string {
  return `${trimSlash(cdnUrl)}/hotline-loader.js`;
}

/**
 * The tag-manager / no-module-tag embed.
 *
 * A tag manager can only inject a CLASSIC script, and a classic tag cannot run an
 * ES module — so the ordinary snippet pasted into GTM produces no widget and no
 * error. This loader is classic, and injects the module tag itself. It also pulls
 * `/c/<key>.js` first, so the widget boots from config the page already carries
 * and makes no cross-origin config request at all.
 */
export function loaderSnippet({ publicKey, cdnUrl, apiUrl }: SnippetParams): string {
  return [
    '<script',
    `  src="${loaderUrl(cdnUrl)}"`,
    `  data-public-key="${publicKey}"`,
    `  data-api-base="${trimSlash(apiUrl)}"`,
    '  async',
    '></script>',
  ].join('\n');
}

/**
 * The single-tag embed.
 *
 * `/c/<key>.js?loader=1` returns the instance's config AND injects the module
 * bundle itself, so the customer's whole integration is one classic tag with no
 * data-attributes and no key repeated in their HTML. Because it is a classic
 * script it is not CORS-gated at all.
 *
 * Tradeoff worth stating to the customer: the bundle cannot start downloading
 * until this script has executed, so it costs one serialized round trip that the
 * two-tag form avoids.
 */
export function oneTagSnippet({ publicKey, cdnUrl }: SnippetParams): string {
  const cdn = trimSlash(cdnUrl);
  return `<script src="${cdn}/c/${publicKey}.js?loader=1&cdnBase=${encodeURIComponent(cdn)}"></script>`;
}

/**
 * The same-origin path proxy — the only configuration with NO CORS at all.
 *
 * The customer proxies a path on their own domain to the API, so every request
 * the widget makes is same-origin: no Origin header, no preflight, no allowlist
 * to keep in sync. It is the most robust integration available and the one to
 * recommend to anyone who already runs a reverse proxy.
 *
 * Two things must be in the snippet or it fails in ways that are hard to spot:
 * `/socket.io/` has to be proxied too (otherwise feedback works and voice
 * silently does not), and the real client IP has to be forwarded (otherwise every
 * visitor shares one rate-limit bucket, because the API only sees the proxy).
 */
export function pathProxySnippet({ publicKey, cdnUrl, apiUrl }: SnippetParams): string {
  const api = trimSlash(apiUrl);
  const host = new URL(api).host;
  return [
    '# 1. Proxy a path on YOUR domain to the Hotline API (nginx shown).',
    '#    Forward the real client IP: without it the API sees only your server,',
    '#    so all your visitors share a single rate-limit bucket.',
    'location /hotline/ {',
    `    proxy_pass ${api}/;`,
    `    proxy_set_header Host ${host};`,
    '    proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;',
    '}',
    '',
    '# 2. Voice/text agent only — same prefix, WebSocket upgrade.',
    '#    Skip this and feedback works while the live agent silently never connects.',
    'location /hotline/socket.io/ {',
    `    proxy_pass ${api}/socket.io/;`,
    '    proxy_http_version 1.1;',
    '    proxy_set_header Upgrade $http_upgrade;',
    '    proxy_set_header Connection "upgrade";',
    `    proxy_set_header Host ${host};`,
    '    proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;',
    '}',
    '',
    '<!-- 3. Point the widget at your own path. Note: no api.hotline.red anywhere. -->',
    '<script',
    '  type="module"',
    `  src="${trimSlash(cdnUrl)}/hotline.js"`,
    `  data-public-key="${publicKey}"`,
    '  data-api-base="/hotline"',
    '  async',
    '></script>',
    '',
    '<!-- CSP becomes trivial: connect-src \'self\' — nothing vendor-specific. -->',
  ].join('\n');
}

export interface InstallSnippet {
  id:
    | 'embed'
    | 'one-tag'
    | 'loader'
    | 'floating'
    | 'bookmarklet'
    | 'path-proxy'
    | 'csp'
    | 'diagnose';
  label: string;
  language: 'html' | 'javascript' | 'text';
  description: string;
  code: string;
}

/** Build all four snippets for an instance in display order. */
export function buildInstallSnippets(params: SnippetParams): InstallSnippet[] {
  return [
    {
      id: 'embed',
      label: 'Script embed',
      language: 'html',
      description:
        'Drop this <script type="module"> tag into your page <head>. Auto-mounts on load.',
      code: embedSnippet(params),
    },
    {
      id: 'one-tag',
      label: 'Single tag',
      language: 'html',
      description:
        'One classic tag, no attributes: it carries the config and loads the widget. Costs one extra serialized round trip.',
      code: oneTagSnippet(params),
    },
    {
      id: 'loader',
      label: 'Tag manager',
      language: 'html',
      description:
        'A classic script for Google Tag Manager and anywhere you cannot set type="module". Also loads config without CORS.',
      code: loaderSnippet(params),
    },
    {
      id: 'floating',
      label: 'Floating launcher',
      language: 'html',
      description:
        'The same module script with data-mode="floating" — adds a floating feedback button.',
      code: floatingSnippet(params),
    },
    {
      id: 'bookmarklet',
      label: 'Bookmarklet',
      language: 'javascript',
      description:
        'Drag this link to your bookmarks bar to launch the widget on any page.',
      code: bookmarkletSnippet(params),
    },
    {
      id: 'diagnose',
      label: 'Verify the install',
      language: 'text',
      description:
        'Not working? This says exactly why — it answers even when the origin allowlist is what is wrong.',
      code: diagnoseSnippet(params),
    },
    {
      id: 'path-proxy',
      label: 'Same-origin proxy (no CORS)',
      language: 'text',
      description:
        'If you run a reverse proxy: the most robust option. No CORS, no preflight, no allowed-origins to maintain.',
      code: pathProxySnippet(params),
    },
    {
      id: 'csp',
      label: 'Content-Security-Policy',
      language: 'text',
      description:
        'Add these directives to your site CSP so the widget can load and connect.',
      code: cspBlock(params),
    },
  ];
}
